/**
 * Display and layout config for dashboard KPI cards.
 */

import type { DashboardStatsQuery } from '@/types/dashboard.types';
import type { ComparisonPeriod } from '@/utils/dashboard-stats-date-range-utils';

/** Cards without a family on a grouped dashboard stay visible at the end. */
export const UNGROUPED_KPI_LABEL = 'Autres';

/** Cards shown per group before the expand control. */
export const COLLAPSED_KPI_CARD_COUNT = 6;

/** Stagger delay between card enter animations. */
export const KPI_CARD_STAGGER_MS = 50;

/** Default icon / title colors for KPI cards. */
export const KPI_CARD_COLOR = {
  icon: 'text-[var(--color-2-nuance-1)]',
  bg: 'bg-[color-mix(in_srgb,var(--color-2-nuance-1)_15%,transparent)]',
  title: 'text-[var(--color-2-nuance-2)]',
} as const;

/** React Query key factory for dashboard KPI stats. */
export const KPI_QUERY_KEYS = {
  dashboard: (query: DashboardStatsQuery) => ['dashboardStats', query] as const,
};

/** Dashboard IE element ids that gate the KPI filter bar. */
export const KPI_FILTER_ELEMENT_IDS = {
  period: 'filter-period',
  dateRange: 'filter-date-range',
  comparison: 'filter-comparison',
  establishment: 'filter-establishment',
} as const;

/** French UI copy for the KPI section and grid. */
export const KPI_LABELS = {
  retry: 'Réessayer',
  loading: 'Chargement...',
  seeLess: 'Voir moins',
  filter: 'Filtrer',
  cancel: 'Annuler',
  finish: 'Terminer',
  edit: 'Modifier',
  seeMore: (count: number) => `Voir plus (${count} autres)`,
  hiddenCount: (count: number) => `(${count} masquée${count > 1 ? 's' : ''})`,
};

/** Comparison period chips (mutually exclusive with a custom date range). */
export const KPI_COMPARISON_OPTIONS: { value: ComparisonPeriod; label: string }[] = [
  { value: 'none', label: 'Aucune' },
  { value: 'previous_week', label: 'Sem. préc.' },
  { value: 'previous_month', label: 'Mois préc.' },
  { value: 'previous_quarter', label: 'Trim. préc.' },
  { value: 'previous_year', label: 'Année préc.' },
];

/** Labels for the KPI filter groups. */
export const KPI_FILTER_LABELS = {
  period: 'Période',
  customDate: 'Date personnalisée',
  comparison: 'Comparaison',
  status: 'Statut',
  establishment: 'Établissement',
  searchEstablishment: 'Rechercher un établissement...',
} as const;
